// src/pages/Documents/DocumentsObligatoiresPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaDownload,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaFileAlt,
  FaFileImage,
  FaFilePdf,
  FaFolderOpen,
  FaInfoCircle,
  FaLock,
  FaPrint,
  FaShieldAlt,
  FaTimes,
} from "react-icons/fa";

import { getCoffreFortSegments } from "../../services/segmentDossierServices";
import useScrollToTop from "../../hooks/useScollToTop";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3317";
const FALLBACK = "/img/article_placeholder.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function sanitizeHtml(html = "") {
  return DOMPurify.sanitize(html || "", {
    USE_PROFILES: {
      html: true,
    },
    FORBID_ATTR: ["style"],
  });
}

function isExternalUrl(url = "") {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function isDriveUrl(url = "") {
  try {
    const parsed = new URL(url);
    return parsed.hostname.includes("drive.google.com");
  } catch {
    return false;
  }
}

function resolveFileUrl(url = "") {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    isExternalUrl(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  if (cleanUrl.startsWith("/img/")) {
    return cleanUrl;
  }

  if (cleanUrl.startsWith("/uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}${cleanUrl}`;
  }

  if (cleanUrl.startsWith("uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}/${cleanUrl}`;
  }

  if (cleanUrl.startsWith("/")) {
    return cleanUrl;
  }

  return `${API_BASE.replace(/\/$/, "")}/${cleanUrl}`;
}

function getFileKind(file) {
  const candidates = [file?.lien_fichier, file?.nom_fichier];

  for (const candidate of candidates) {
    if (!candidate) continue;

    const clean = String(candidate).split("?")[0].toLowerCase();

    if (clean.endsWith(".pdf")) return "pdf";

    if (
      clean.endsWith(".jpg") ||
      clean.endsWith(".jpeg") ||
      clean.endsWith(".png") ||
      clean.endsWith(".gif") ||
      clean.endsWith(".webp") ||
      clean.endsWith(".svg")
    ) {
      return "image";
    }
  }

  return "other";
}

function getFileTypeIcon(file) {
  const kind = getFileKind(file);

  if (kind === "pdf") return FaFilePdf;
  if (kind === "image") return FaFileImage;

  return FaFileAlt;
}

const DocumentsSkeleton = () => {
  return (
    <main className="min-h-screen bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] dark:bg-slate-950">
      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-56 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="mt-12 space-y-5">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="h-56 animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>
      </div>
    </main>
  );
};

const EmptyState = () => {
  return (
    <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/80 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaFolderOpen />
      </div>

      <h2 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Aucun document publié
      </h2>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        Les documents obligatoires et informations légales du club apparaîtront
        ici dès qu’ils seront mis à disposition.
      </p>
    </div>
  );
};

const ErrorState = ({ message }) => {
  return (
    <div className="rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
      <div className="flex items-center justify-center gap-3">
        <FaExclamationTriangle />
        {message}
      </div>
    </div>
  );
};

const SummaryCard = ({ icon: Icon, value, label }) => {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
      <Icon className="mx-auto mb-3 text-2xl text-primary" />

      <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
        {value}
      </p>

      <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </p>
    </div>
  );
};

const FileCard = ({ file, onPreview }) => {
  const kind = getFileKind(file);
  const Icon = getFileTypeIcon(file);
  const fileUrl = resolveFileUrl(file?.lien_fichier);
  const drive = isDriveUrl(fileUrl);
  const isPreviewable = !drive && (kind === "image" || kind === "pdf");

  const kindLabel = drive
    ? "Google Drive"
    : kind === "pdf"
    ? "PDF"
    : kind === "image"
    ? "Image"
    : "Document";

  const actionLabel = drive
    ? "Ouvrir Drive"
    : isPreviewable
    ? "Aperçu"
    : "Ouvrir";

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-[1.7rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1.5 hover:shadow-xl dark:border-white/10 dark:bg-slate-900">
      <button
        type="button"
        onClick={() => onPreview(file)}
        className="relative block aspect-[4/3] w-full overflow-hidden bg-slate-950 text-left"
        title={actionLabel}
      >
        {kind === "image" && !drive ? (
          <img
            src={fileUrl}
            alt={file?.nom_fichier || "Document"}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
            onError={(event) => {
              event.currentTarget.src = FALLBACK;
            }}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.24),transparent_36%),linear-gradient(135deg,#020617,#111827)] px-5 text-center">
            <Icon className="text-5xl text-white drop-shadow-xl" />

            <span className="mt-4 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white/75 backdrop-blur-xl">
              {kindLabel}
            </span>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />

        <div className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/35 bg-white/95 text-slate-950 shadow-2xl backdrop-blur-xl transition group-hover:bg-primary group-hover:text-white">
          {isPreviewable ? (
            <Icon className="text-base" />
          ) : (
            <FaExternalLinkAlt className="text-sm" />
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="mb-3 inline-flex rounded-full border border-white/15 bg-white/15 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-white/75 backdrop-blur-xl">
            {kindLabel}
          </p>

          <h3 className="line-clamp-2 font-barlow text-xl font-bold leading-tight text-white drop-shadow-lg">
            {file?.nom_fichier || "Document"}
          </h3>
        </div>
      </button>

      <div className="flex flex-1 flex-col p-4 pb-6">
        {file?.detail_fichier ? (
          <p className="line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {file.detail_fichier}
          </p>
        ) : (
          <p className="text-sm italic leading-6 text-slate-500 dark:text-slate-400">
            Document consultable par le public.
          </p>
        )}

        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5">
          <button
            type="button"
            onClick={() => onPreview(file)}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-4 py-2.5 text-xs font-bold text-primary transition hover:-translate-y-0.5 hover:bg-primary hover:text-white"
          >
            <Icon className="text-sm" />
            {actionLabel}
          </button>

          {fileUrl && (
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 transition hover:text-primary dark:text-slate-400"
            >
              <FaExternalLinkAlt className="text-[10px]" />
              Nouvel onglet
            </a>
          )}
        </div>
      </div>

      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-90 transition group-hover:opacity-100"
      />
    </article>
  );
};

const SegmentSection = ({ segment, onPreview }) => {
  const safeContent = useMemo(
    () => sanitizeHtml(segment?.segment_content || ""),
    [segment?.segment_content]
  );

  const files = Array.isArray(segment?.fichiers) ? segment.fichiers : [];

  return (
    <section className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90">
      <div className="border-b border-slate-200 bg-slate-50/80 p-5 dark:border-white/10 dark:bg-white/5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-primary/20">
              <FaInfoCircle />
            </div>

            <div>
              <h2 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
                {segment?.segment_name || "Documents"}
              </h2>

              {safeContent && (
                <div
                  className={cn(
                    "mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300",
                    "prose prose-slate max-w-none dark:prose-invert",
                    "prose-p:my-1 prose-a:font-bold prose-a:text-primary",
                    "dark:[&_strong]:!text-white dark:[&_b]:!text-white"
                  )}
                  dangerouslySetInnerHTML={{
                    __html: safeContent,
                  }}
                />
              )}
            </div>
          </div>

          <div className="shrink-0 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-500 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            {files.length} document{files.length > 1 ? "s" : ""}
          </div>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        {files.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm italic text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
            Aucun document n’est disponible dans cette catégorie pour le moment.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {files.map((file) => (
              <FileCard key={file.id} file={file} onPreview={onPreview} />
            ))}
          </div>
        )}
      </div>

      <div
        aria-hidden
        className="h-1.5 w-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
      />
    </section>
  );
};

const PreviewModal = ({ file, onClose }) => {
  const fileUrl = resolveFileUrl(file?.lien_fichier);
  const kind = getFileKind(file);
  const Icon = getFileTypeIcon(file);

  useEffect(() => {
    if (!file) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [file, onClose]);

  if (!file) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/80 px-3 py-5 backdrop-blur-sm sm:px-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-slate-950 text-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-white/10 bg-slate-950/95 p-4 backdrop-blur-xl sm:p-5">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-primary">
              <Icon />
            </div>

            <div className="min-w-0">
              <h3 className="line-clamp-2 font-barlow text-lg font-bold leading-tight text-white sm:text-xl">
                {file?.nom_fichier || "Document"}
              </h3>

              {file?.detail_fichier && (
                <p className="mt-1 line-clamp-2 text-xs leading-5 text-white/55">
                  {file.detail_fichier}
                </p>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {fileUrl && (
              <a
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
                className="hidden items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold text-white transition hover:bg-white hover:text-slate-950 sm:inline-flex"
              >
                <FaExternalLinkAlt className="text-[10px]" />
                Nouvel onglet
              </a>
            )}

            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-slate-900">
          {kind === "image" && (
            <div className="flex min-h-[60vh] items-center justify-center p-4">
              <img
                src={fileUrl}
                alt={file?.nom_fichier || "Document"}
                className="max-h-[78vh] w-full object-contain"
                onError={(event) => {
                  event.currentTarget.src = FALLBACK;
                }}
              />
            </div>
          )}

          {kind === "pdf" && (
            <iframe
              title={file?.nom_fichier || "Document PDF"}
              src={fileUrl}
              className="h-[78vh] w-full border-0 bg-white"
            />
          )}

          {kind === "other" && (
            <div className="flex min-h-[55vh] items-center justify-center p-6 text-center">
              <div className="max-w-md">
                <Icon className="mx-auto text-5xl text-primary" />

                <h4 className="mt-5 font-barlow text-2xl font-bold text-white">
                  Aperçu non disponible
                </h4>

                <p className="mt-3 text-sm leading-7 text-white/65">
                  Ce type de document ne peut pas être prévisualisé directement.
                  Vous pouvez l’ouvrir dans un nouvel onglet.
                </p>

                {fileUrl && (
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
                  >
                    <FaExternalLinkAlt className="text-xs" />
                    Ouvrir le document
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {fileUrl && (
          <div className="border-t border-white/10 bg-slate-950 p-4 sm:hidden">
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-3 text-xs font-bold text-white"
            >
              <FaExternalLinkAlt className="text-[10px]" />
              Ouvrir dans un nouvel onglet
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const DocumentsObligatoiresPage = () => {
  useScrollToTop();

  const navigate = useNavigate();

  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchSegments = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getCoffreFortSegments();
        const safeSegments = Array.isArray(data) ? data : [];

        safeSegments.sort(
          (a, b) => Number(a?.ordre_segment || 0) - Number(b?.ordre_segment || 0)
        );

        if (!mounted) return;

        setSegments(safeSegments);
      } catch (fetchError) {
        console.error(fetchError);

        if (mounted) {
          setError(
            "Impossible de charger les documents obligatoires du club pour le moment."
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSegments();

    return () => {
      mounted = false;
    };
  }, []);

  const summary = useMemo(() => {
    const files = segments.reduce((total, segment) => {
      return total + (Array.isArray(segment?.fichiers) ? segment.fichiers.length : 0);
    }, 0);

    return {
      segments: segments.length,
      files,
      pdf: segments.reduce((total, segment) => {
        const list = Array.isArray(segment?.fichiers) ? segment.fichiers : [];

        return (
          total +
          list.filter((file) => getFileKind(file) === "pdf").length
        );
      }, 0),
    };
  }, [segments]);

  const openPreview = (file) => {
    const fileUrl = resolveFileUrl(file?.lien_fichier);
    const kind = getFileKind(file);

    if (!fileUrl) return;

    if (isDriveUrl(fileUrl)) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (kind === "image" || kind === "pdf") {
      setPreviewFile({
        ...file,
        lien_fichier: fileUrl,
      });

      return;
    }

    window.open(fileUrl, "_blank", "noopener,noreferrer");
  };

  const closePreview = () => {
    setPreviewFile(null);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <DocumentsSkeleton />;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <style>{`
        @media print {
          nav,
          footer,
          .no-print {
            display: none !important;
          }

          main {
            padding-top: 0 !important;
            background: #ffffff !important;
          }

          .print-card {
            box-shadow: none !important;
            border: none !important;
          }
        }
      `}</style>

      <div className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <FaArrowLeft className="text-xs" />
            Retour
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
          >
            <FaPrint className="text-sm" />
            Imprimer
          </button>
        </div>

        <section className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaLock />
            Documents publics
          </div>

          <h1 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Documents{" "}
            <span className="text-primary">obligatoires</span>
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Retrouvez les diplômes, attestations, assurances et documents de
            prévention mis à disposition du public par le club.
          </p>

          <p className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2 text-xs font-bold text-slate-500 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            <FaShieldAlt className="text-primary" />
            Transparence, encadrement qualifié et sécurité des pratiquants.
          </p>
        </section>

        {!error && segments.length > 0 && (
          <section className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-3">
            <SummaryCard
              icon={FaFolderOpen}
              value={summary.segments}
              label="Catégories"
            />

            <SummaryCard
              icon={FaFileAlt}
              value={summary.files}
              label="Documents"
            />

            <SummaryCard
              icon={FaFilePdf}
              value={summary.pdf}
              label="PDF"
            />
          </section>
        )}

        {error && (
          <div className="mt-12">
            <ErrorState message={error} />
          </div>
        )}

        {!error && segments.length === 0 && (
          <div className="mt-12">
            <EmptyState />
          </div>
        )}

        {!error && segments.length > 0 && (
          <div className="print-card mt-12 space-y-6">
            {segments.map((segment) => (
              <SegmentSection
                key={segment.id}
                segment={segment}
                onPreview={openPreview}
              />
            ))}
          </div>
        )}
      </div>

      {previewFile && (
        <PreviewModal file={previewFile} onClose={closePreview} />
      )}
    </main>
  );
};

export default DocumentsObligatoiresPage;